/**
 * AddToCalendar component for events
 * Uses shadcn/ui components directly
 */

'use client';

import { useState } from 'react';
import { CalendarPlus, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';

interface EventData {
  title: string;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  location?: string | null;
  description?: string | null;
  url?: string | null;
}

interface AddToCalendarProps {
  event: EventData;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'default';
  className?: string;
}

// Format date for calendar URLs (YYYYMMDDTHHMMSS)
function formatDateForCalendar(date: Date | string | null | undefined): string {
  if (!date) return '';
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
  } catch {
    return '';
  }
}

// Format date as ISO string
function formatDateISO(date: Date | string | null | undefined): string {
  if (!date) return '';
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toISOString();
  } catch {
    return '';
  }
}

// Transform URLs containing /request/access to base domain only
function transformUrl(url: string | null | undefined): string {
  if (!url) return '';
  try {
    const urlObj = new URL(url);
    // If URL contains /request/access, transform to base domain only
    if (urlObj.pathname.includes('/request/access')) {
      return `${urlObj.protocol}//${urlObj.host}`;
    }
    return url;
  } catch {
    return url || '';
  }
}

function generateCalendarLinks(event: EventData) {
  const title = encodeURIComponent(event.title || 'Event');
  const startDate = formatDateForCalendar(event.startDate);

  // Calculate end date - use provided endDate or default to 1 hour after start
  let endDate: string;
  if (event.endDate) {
    endDate = formatDateForCalendar(event.endDate);
  } else if (event.startDate) {
    const start = typeof event.startDate === 'string' ? new Date(event.startDate) : event.startDate;
    const end = new Date(start.getTime() + 60 * 60 * 1000); // Add 1 hour
    endDate = formatDateForCalendar(end);
  } else {
    endDate = '';
  }

  const location = encodeURIComponent(event.location || '');
  const description = encodeURIComponent((event.description || '').slice(0, 500));
  const transformedUrl = transformUrl(event.url);
  const url = encodeURIComponent(transformedUrl);

  const startISO = formatDateISO(event.startDate);
  const endISO = event.endDate
    ? formatDateISO(event.endDate)
    : event.startDate
      ? (() => {
          const start =
            typeof event.startDate === 'string' ? new Date(event.startDate) : event.startDate;
          return new Date(start.getTime() + 60 * 60 * 1000).toISOString();
        })()
      : '';

  return {
    google: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${description}&location=${location}`,
    outlook: `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${startISO}&enddt=${endISO}&location=${location}&body=${description}`,
    yahoo: `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${title}&st=${startDate}&dur=0100&desc=${description}&in_loc=${location}`,
    apple: generateICSFile(event),
    wechat: generateICSFile(event),
    ics: generateICSFile(event),
  };
}

function generateICSFile(event: EventData): string {
  const startDate = event.startDate
    ? typeof event.startDate === 'string'
      ? new Date(event.startDate)
      : event.startDate
    : new Date();

  const endDate = event.endDate
    ? typeof event.endDate === 'string'
      ? new Date(event.endDate)
      : event.endDate
    : new Date(startDate.getTime() + 60 * 60 * 1000);

  const formatICSDate = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const title = (event.title || 'Event').replace(/,/g, '\\,').replace(/;/g, '\\;');
  const location = (event.location || '').replace(/,/g, '\\,').replace(/;/g, '\\;');
  const description = (event.description || '')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;')
    .slice(0, 500);
  const transformedUrl = transformUrl(event.url);
  const url = transformedUrl || '';

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//UIC Solutions//Event//EN',
    'BEGIN:VEVENT',
    `DTSTART:${formatICSDate(startDate)}`,
    `DTEND:${formatICSDate(endDate)}`,
    `SUMMARY:${title}`,
    location ? `LOCATION:${location}` : '',
    description ? `DESCRIPTION:${description}` : '',
    url ? `URL:${url}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\r\n');

  return `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
}

export function AddToCalendar({
  event,
  variant = 'outline',
  size = 'default',
  className = '',
}: AddToCalendarProps) {
  const links = generateCalendarLinks(event);
  const [open, setOpen] = useState(false);

  // Map size to icon size for better visual consistency
  // Using size-* classes which work better with Tailwind and Button component
  // Proportions: sm (h-8) → 3.5, md/default (h-9) → 4, lg (h-10) → 6
  const iconSizeClass = {
    sm: 'size-[14px]',
    md: 'size-4',
    lg: 'size-6',
    default: 'size-4',
  }[size];

  const handleICSDownload = () => {
    const icsContent = generateICSFile(event).replace('data:text/calendar;charset=utf-8,', '');
    const blob = new Blob([decodeURIComponent(icsContent)], {
      type: 'text/calendar;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(event.title || 'event').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setOpen(false);
  };

  const handleAppleCalendar = () => {
    // Apple Calendar uses .ics files, same as download
    handleICSDownload();
  };

  const handleWeChatCalendar = () => {
    // WeChat Calendar uses .ics files
    handleICSDownload();
  };

  const handleLinkClick = () => {
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant={variant}
              size={size}
              className={className}
              onClick={e => {
                // Stop propagation to prevent card click, but allow PopoverTrigger to work
                e.stopPropagation();
              }}
            >
              <CalendarPlus className={iconSizeClass} />
              <span className="sr-only">Add to Calendar</span>
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>Add to Calendar</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        align="end"
        side="bottom"
        className="w-56 p-1"
        onClick={e => {
          // Stop propagation to prevent card click when clicking inside popover
          e.stopPropagation();
        }}
      >
        <div className="flex flex-col">
          <a
            href={links.google}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLinkClick}
            className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Google Calendar
          </a>
          <a
            href={links.outlook}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLinkClick}
            className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="#0078D4">
              <path d="M7.5 2.25V12l7-1.875V.375L7.5 2.25zm9 2.25L24 3v9l-7.5-1.875V4.5zm0 6.75L24 12v9l-7.5-1.875v-8.25zM7.5 13.5L0 15.375V24l7.5-1.875V13.5z" />
            </svg>
            Outlook
          </a>
          <a
            href={links.yahoo}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLinkClick}
            className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="#6001D2">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
            </svg>
            Yahoo Calendar
          </a>
          <button
            onClick={handleAppleCalendar}
            className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer text-left w-full"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="#000000">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.41-1.09-.5-2.08-.96-3.24-1.44-1.41-.6-2.35-.96-3.65-1.99C4.45 15.25 2.92 12.77 2.29 9.79c-.42-2.01-.2-3.84.78-5.72.84-1.63 2.05-2.77 3.64-3.58 1.56-.8 3.12-.95 4.77-.42 1.85.6 3.6 1.44 5.37 2.31 1.43.7 2.76 1.48 4.14 2.26.45.26.96.57 1.15 1.05.16.43.14.88-.03 1.31-.18.45-.5.81-.88 1.09-.56.41-1.17.73-1.75 1.09-.19.12-.4.26-.56.44-.17.19-.3.4-.4.64-.1.25-.15.52-.08.78.07.28.25.52.5.68.5.33 1.05.58 1.57.88.19.11.38.24.52.4.15.17.25.37.3.58.05.2.05.41.02.62-.05.35-.2.66-.4.93-.4.54-.9.99-1.4 1.44z" />
            </svg>
            Apple Calendar
          </button>
          <button
            onClick={handleWeChatCalendar}
            className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer text-left w-full"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="#07C160">
              <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 7.68 7.68 0 0 0 2.108.293c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.464-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm-2.907 3.31c-1.409 0-2.546 1.16-2.546 2.588 0 .303.048.595.135.873.571 2.314 2.445 3.635 4.74 3.826.417.038.839.057 1.262.057 1.33 0 2.603-.321 3.756-.93a.722.722 0 0 0 .392-.63.722.722 0 0 0-.508-.687c-1.784-.69-3.807-.632-5.365.05-1.393.61-2.768.13-3.72-1.18a2.588 2.588 0 0 1-.998-1.807zm16.31 1.592c-.41-.006-.819-.03-1.222-.03-4.281 0-7.605 2.695-7.605 6.287 0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098c.62.19 1.274.293 1.94.293 4.8 0 8.691-3.288 8.691-7.342 0-4.053-3.891-7.342-8.691-7.342zm-1.504 9.67c-.642 0-1.162-.529-1.162-1.18a1.17 1.17 0 0 1 1.162-1.178c.641 0 1.162.529 1.162 1.178 0 .651-.52 1.18-1.162 1.18zm-5.813 0c-.642 0-1.162-.529-1.162-1.18a1.17 1.17 0 0 1 1.162-1.178c.641 0 1.162.529 1.162 1.178 0 .651-.52 1.18-1.162 1.18z" />
            </svg>
            WeChat Calendar
          </button>
          <button
            onClick={handleICSDownload}
            className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer text-left w-full"
          >
            <Download className="h-4 w-4" />
            Download .ics file
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
